<template>
    <Row
        hoverable
        class="RiskProfileTableRow"
        @click="$emit('edit', riskProfile.id)"
    >
        <Cell class="RiskProfileTableRow__riskProfileName">
            <div class="RiskProfileTableRow__riskProfileNameContent">
                <EidIcon
                    v-if="showRiskProfileTypeIcon"
                    :icon-src="riskProfileIconSrc"
                    :svg-props="{color: 'var(--eidQualityBlack-75)'}"
                    data-test-id="RiskProfileTableRow_riskProfileTypeIcon"
                />
                <span data-test-id="RiskProfileTableRow_riskProfileNameText">{{ riskProfile.displayName }}</span>
            </div>
        </Cell>

        <Cell>
            <div class="RiskProfileTableRow__assignment">
                <div v-if="assignmentRuleType === AssignmentRuleType.all">
                    All Entities
                </div>
                <div v-else-if="assignmentRuleType === AssignmentRuleType.selected">
                    <div>Rule-Based Entities</div>
                    <div class="RiskProfileTableRow__entitiesAssigned" @click.stop="goToEntityTable">
                        {{ ruleBasedEntitiesLabel }}
                    </div>
                </div>
                <!-- TODO[before merge]: update to infor matches mockup -->
                <div v-else-if="assignmentRuleType === AssignmentRuleType.single && riskProfile.assignedEntityInfo">
                    <div class="RiskProfileTableRow__entityDisplayName">
                        {{ riskProfile.assignedEntityInfo.displayName }}
                    </div>
                    <div class="RiskProfileTableRow__entityEmail">
                        {{ riskProfile.assignedEntityInfo.email }}
                    </div>
                </div>
            </div>
        </Cell>

        <Cell>
            <span class="RiskProfileTableRow__profileType">
                {{ profileTypeLabel }}
            </span>
        </Cell>

        <Cell class="RiskProfileTableRow__numberOfRequirements">
            {{ riskProfile.numberOfCoverageTypes }}
        </Cell>
        <Cell
            class="RiskProfileTableRow__status"
            :class="{'RiskProfileTableRow__status--active': riskProfile.active}"
        >
            <div class="RiskProfileTableRow__statusChip">
                {{ status }}
            </div>
        </Cell>

        <Cell class="RiskProfileTableRow__actionsCell">
            <RbacDisplayGuard :features="[RbacFeature.decisioningCreateRiskProfile]">
                <NewButton
                    variant="flat"
                    size="icon-sm"
                    color="neutral"
                    class="RiskProfileTableRow__copyIcon"
                    aria-label="copy"
                    @click.stop="$emit('copy', riskProfile)"
                >
                    <EidIcon
                        :icon-src="faCopy"
                    />
                </NewButton>
            </RbacDisplayGuard>
            <RbacDisplayGuard :features="[RbacFeature.decisioningDeleteRiskProfile]">
                <NewButton
                    variant="flat"
                    size="icon-sm"
                    color="neutral"
                    class="RiskProfileTableRow__deleteIcon"
                    aria-label="delete"
                    @click.stop="$emit('delete', riskProfile.id)"
                >
                    <EidIcon
                        :icon-src="faTimes"
                    />
                </NewButton>
            </RbacDisplayGuard>
        </Cell>
    </Row>
</template>

<style lang="scss" scoped>
.RiskProfileTableRow {
    cursor: pointer;

    .TableCell {
        // this is td element which means height is basically treated as min-height
        height: 65px;
    }

    &__riskProfileNameContent {
        display: flex;
        align-items: center;
        color: var(--eidTrustGreen);

        .EidIcon {
            width: 20px;
            height: 20px;
            margin-right: 10px;
        }
    }

    &__numberOfRequirements {
        text-align: center;
    }

    &__status {
        display: flex;
        align-items: center;
    }

    &__statusChip {
        font-size: 12px;
        border: 1px solid;
        border-radius: 12px;
        padding: 2px 8px;
        color: var(--eidSecureBlue-75);
    }

    &__status--active {
        .RiskProfileTableRow__statusChip {
            border-color: var(--eidTrustGreen-25);
            background-color: var(--eidTrustGreen-10);
            color: var(--eidTrustGreen);
        }
    }

    &__actionsCell {
        text-align: end;
    }

    &__copyIcon {
        margin-right: 4px;
    }

    &__entitiesAssigned {
        font-size: 12px;
        cursor: pointer;
        width: fit-content;
        color: var(--eidSecureBlue-75);

        &:hover {
            opacity: 0.7;
        }
    }

    &__entityDisplayName {
        color: var(--eidTrustGreen);
    }

    &__entityEmail {
        font-size: 12px;
        color: var(--eidSecureBlue-75);
    }
}
</style>
<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faCopy, faTimes } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisplayGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { AssignmentRuleSingle, AssignmentRuleType } from '@evidentid/tprm-portal-lib/models/decisioning/AssignmentRule.model';
    import { RiskProfile, RiskProfileType } from '@evidentid/tprm-portal-lib/models/decisioning/RiskProfile.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { getRiskProfileTypeIconSrc } from '@/modules/decisioning-criteria/utils/get-risk-profile-type-icon-src/getRiskProfileTypeIconSrc';

    export default defineComponent({
        name: 'RiskProfileTableRow',
        components: {
            Cell,
            EidIcon,
            NewButton,
            RbacDisplayGuard,
            Row,
        },
        props: {
            riskProfile: {
                type: Object as PropType<RiskProfile>,
                required: true,
            },
            showRiskProfileTypeIcon: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'copy', 'delete', 'edit' ],
        data() {
            return {
                faCopy,
                faTimes,
                AssignmentRuleType,
                RbacFeature,
            };
        },
        computed: {
            status(): string {
                return this.riskProfile.active ? 'Active' : 'Discover';
            },
            riskProfileIconSrc(): string {
                return getRiskProfileTypeIconSrc(this.riskProfile.ccgType);
            },
            ruleBasedEntitiesLabel(): string {
                const numberOfEntities = this.riskProfile.numberOfInsuredsAssigned || 0;
                return `${this.riskProfile.numberOfInsuredsAssigned} ${numberOfEntities > 1 ? ' entities' : ' entity'}`;
            },
            entityInfo(): { displayName: string, email: string } | null {
                return this.riskProfile.assignedEntityInfo;
            },
            assignmentRuleType(): AssignmentRuleType | null {
                return this.riskProfile.assignmentRule?.type || null;
            },
            entityEmailLabel(): string {
                if (this.assignmentRuleType === AssignmentRuleType.single) {
                    return (this.riskProfile.assignmentRule as AssignmentRuleSingle).entityId || '';
                }
                return '';
            },
            profileTypeLabel(): string {
                const map = {
                    [RiskProfileType.collateral]: 'Collateral',
                    [RiskProfileType.default]: 'Basic',
                };
                return map[this.riskProfile.ccgType] || '';
            },
        },
        methods: {
            goToEntityTable(): void {
                this.$router.push({
                    name: 'dashboard',
                    params: {
                        rpId: this.$route.params.rpId,
                    },
                    query: {
                        coverageCriteriaGroupId: this.riskProfile.id,
                    },
                });
            },
        },
    });
</script>
